import { useEffect, useState, useRef } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import { useAuth } from "@/shared/lib/auth";
import { PageHeader, Btn, Input, Field } from "@/shared/components/UI";
import FileUpload from "@/shared/components/FileUpload";
import { Plus, Trash2, X, Sparkles, Upload, Loader2, AlertTriangle, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", contact_person: "", phone: "", email: "", address: "", notes: "", gstin: "", state_name: "", state_code: "" };

export default function Clients() {
  const { user: me } = useAuth();
  const canCreate = me && (me.role === "admin" || me.role === "scope_user");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [f, setF] = useState(EMPTY);

  function load() { api.get("/clients").then((r) => setItems(r.data)); }
  useEffect(load, []);

  async function save(e) {
    e.preventDefault();
    try { await api.post("/clients", f); toast.success("Saved"); setOpen(false); load(); setF(EMPTY); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  async function del(id) { if (confirm("Delete client?")) { await api.delete(`/clients/${id}`); load(); } }

  return (
    <div className="p-6 lg:p-10" data-testid="clients-page">
      <PageHeader kicker="Customers" title="Clients">
        {canCreate && (
          <>
            <Btn variant="secondary" onClick={() => setShowAI(true)} data-testid="ai-create-client-btn"
                 className="border-violet-300 text-violet-700 hover:bg-violet-50">
              <Sparkles className="w-4 h-4" /> Create with AI
            </Btn>
            <Btn onClick={() => setOpen(true)} data-testid="new-client-btn"><Plus className="w-4 h-4" /> New Client</Btn>
          </>
        )}
      </PageHeader>
      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Contact</th><th className="text-left p-3">Phone</th><th className="text-left p-3">Email</th><th></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-zinc-400">No clients</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-zinc-100">
                <td className="p-3 font-medium">{c.name}</td>
                <td className="p-3">{c.contact_person || "—"}</td>
                <td className="p-3 font-mono text-xs">{c.phone || "—"}</td>
                <td className="p-3 font-mono text-xs">{c.email || "—"}</td>
                <td className="p-3 text-right"><button onClick={() => del(c.id)} className="text-rose-600 p-1.5" data-testid={`client-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white max-w-md w-full p-6 space-y-3 border border-zinc-200">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-xl">New Client</h3>
              <button type="button" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <Field label="Name" required><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="client-name-input" /></Field>
            <Field label="Contact Person"><Input value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })} /></Field>
            <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
            <Field label="Email"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
            <Field label="Address"><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></Field>
            <Field label="GSTIN / UIN"><Input value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value })} placeholder="e.g., 29AAYFM3206E1Z2" data-testid="client-gstin" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="State Name"><Input value={f.state_name} onChange={(e) => setF({ ...f, state_name: e.target.value })} placeholder="e.g., Karnataka" /></Field>
              <Field label="State Code"><Input value={f.state_code} onChange={(e) => setF({ ...f, state_code: e.target.value })} placeholder="e.g., 29" /></Field>
            </div>
            <div className="flex gap-2"><Btn type="submit" data-testid="save-client-btn">Save</Btn><Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn></div>
          </form>
        </div>
      )}

      {showAI && <AIClientModal onClose={() => setShowAI(false)} onDone={() => { setShowAI(false); load(); }} />}
    </div>
  );
}

const blankDraft = () => ({
  company_name: "", contact_name: "", designation: "", mobile: "", email: "",
  gst_number: "", state_code: "", state_name: "", billing_address: "", shipping_address: "",
  project_name: "", project_location: "", project_category: "", estimated_value: "",
  additional_contacts: [], missing_info: [],
});

function AIClientModal({ onClose, onDone }) {
  const [step, setStep] = useState("input");      // input | parsing | review
  const [mode, setMode] = useState("upload");     // upload | text
  const [fileUrl, setFileUrl] = useState("");
  const [text, setText] = useState("");
  const [draft, setDraft] = useState(blankDraft());
  const [missing, setMissing] = useState([]);
  const [alsoProject, setAlsoProject] = useState(true);
  const [busy, setBusy] = useState(false);
  const cancelledRef = useRef(false);
  useEffect(() => () => { cancelledRef.current = true; }, []);

  function setD(patch) { setDraft((d) => ({ ...d, ...patch })); }
  const isMissing = (k) => missing.includes(k);

  async function runExtract(payload) {
    setStep("parsing");
    cancelledRef.current = false;
    try {
      const { data } = await api.post("/clients/ai-extract", payload);
      const jobId = data.job_id;
      const started = Date.now();
      while (!cancelledRef.current && Date.now() - started < 180000) {
        await new Promise((r) => setTimeout(r, 2500));
        if (cancelledRef.current) return;
        const { data: job } = await api.get(`/clients/ai-extract/${jobId}`);
        if (job.status === "done") {
          const dr = { ...blankDraft(), ...(job.draft || {}) };
          setDraft(dr);
          setMissing(dr.missing_info || []);
          setStep("review");
          toast.success("Draft client profile ready — review & approve");
          return;
        }
        if (job.status === "error") { toast.error(job.error || "AI extraction failed"); setStep("input"); return; }
      }
      if (!cancelledRef.current) { toast.error("AI is taking too long. Please try again."); setStep("input"); }
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "AI extraction failed");
      setStep("input");
    }
  }

  async function approve() {
    if (!draft.company_name.trim() && !draft.contact_name.trim()) {
      toast.error("Company name or contact name is required"); return;
    }
    // Capture details the Client model has no dedicated field for, into notes.
    const noteLines = [];
    if (draft.designation) noteLines.push(`Designation: ${draft.designation}`);
    if (draft.shipping_address) noteLines.push(`Shipping address: ${draft.shipping_address}`);
    if (draft.estimated_value) noteLines.push(`Estimated value: ${draft.estimated_value}`);
    (draft.additional_contacts || []).forEach((c) => {
      const parts = [c.name, c.designation, c.phone, c.email].filter(Boolean).join(", ");
      if (parts) noteLines.push(`Contact: ${parts}`);
    });
    const clientBody = {
      name: draft.company_name || draft.contact_name,
      contact_person: draft.contact_name,
      phone: draft.mobile,
      email: draft.email,
      address: draft.billing_address,
      gstin: draft.gst_number,
      state_name: draft.state_name,
      state_code: draft.state_code,
      notes: noteLines.join("\n"),
    };
    setBusy(true);
    try {
      const { data: client } = await api.post("/clients", clientBody);
      let msg = `Client "${client.name}" created`;
      if (alsoProject && draft.project_name.trim()) {
        const desc = [draft.project_category && `Category: ${draft.project_category}`,
          draft.estimated_value && `Est. value: ${draft.estimated_value}`].filter(Boolean).join(" · ");
        await api.post("/projects", {
          name: draft.project_name,
          client_id: client.id,
          location: draft.project_location,
          description: desc,
          requirements: draft.project_category,
        });
        msg += ` + draft project "${draft.project_name}"`;
      }
      toast.success(msg);
      onDone();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Could not create client");
    } finally { setBusy(false); }
  }

  const fieldCls = (k) => `w-full px-3 py-2 border rounded-md text-sm ${isMissing(k) ? "border-amber-300 bg-amber-50/40" : "border-zinc-300"}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="ai-client-modal">
      <div className="bg-white w-full max-w-3xl max-h-[92vh] flex flex-col border border-zinc-200">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200 flex-shrink-0 bg-gradient-to-r from-violet-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-600 flex items-center justify-center"><Sparkles className="w-5 h-5 text-white" /></div>
            <div>
              <h3 className="font-display font-bold text-xl">AI Client Creation</h3>
              <div className="text-xs text-zinc-500 mt-0.5">Upload an enquiry / RFQ / brief or paste an email — AI builds a draft client profile.</div>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="ai-client-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-y-auto p-6 flex-1">
          {step === "input" && (
            <div className="max-w-xl mx-auto py-4 space-y-5">
              <div className="flex gap-2 p-1 bg-zinc-100 rounded-lg w-fit mx-auto">
                <button onClick={() => setMode("upload")} data-testid="ai-client-mode-upload"
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${mode === "upload" ? "bg-white shadow-sm" : "text-zinc-500"}`}>
                  Upload a file
                </button>
                <button onClick={() => setMode("text")} data-testid="ai-client-mode-text"
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${mode === "text" ? "bg-white shadow-sm" : "text-zinc-500"}`}>
                  Paste text
                </button>
              </div>

              {mode === "upload" ? (
                <div className="space-y-3">
                  <div className="text-center text-sm text-zinc-500">PDF, Word, PPT, Excel, or an image (screenshot of an email works too).</div>
                  <FileUpload value={fileUrl} accept=".pdf,.docx,.pptx,.xlsx,.xls,.csv,.txt,.png,.jpg,.jpeg,.webp"
                              label="Choose enquiry / RFQ / brief"
                              testid="ai-client-file"
                              onChange={(url) => { setFileUrl(url); if (url) runExtract({ file_url: url }); }} />
                </div>
              ) : (
                <div className="space-y-3">
                  <textarea rows="9" value={text} onChange={(e) => setText(e.target.value)} data-testid="ai-client-textarea"
                            className="w-full p-3 border border-zinc-300 rounded-md text-sm"
                            placeholder="Paste the enquiry email or project brief here…" />
                  <Btn onClick={() => text.trim() ? runExtract({ text }) : toast.error("Paste some text first")}
                       data-testid="ai-client-extract-text" className="bg-violet-600 hover:bg-violet-700 w-full justify-center">
                    <Sparkles className="w-4 h-4" /> Extract with AI
                  </Btn>
                </div>
              )}
              <div className="flex items-start gap-2 text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-3">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
                <span>Nothing is saved yet — you'll review and edit every field before creating the client.</span>
              </div>
            </div>
          )}

          {step === "parsing" && (
            <div className="py-20 flex flex-col items-center justify-center gap-4 text-center">
              <Loader2 className="w-10 h-10 text-violet-600 animate-spin" />
              <div className="font-display font-bold text-lg">Reading the document…</div>
              <div className="text-sm text-zinc-500">The AI is extracting the client &amp; project details. This usually takes 10–30 seconds.</div>
            </div>
          )}

          {step === "review" && (
            <div className="space-y-6">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">Client Details</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <RF label="Company Name *" k="company_name"><input value={draft.company_name} onChange={(e) => setD({ company_name: e.target.value })} className={fieldCls("company_name")} data-testid="ai-client-company" /></RF>
                  <RF label="Contact Name" k="contact_name"><input value={draft.contact_name} onChange={(e) => setD({ contact_name: e.target.value })} className={fieldCls("contact_name")} /></RF>
                  <RF label="Designation" k="designation"><input value={draft.designation} onChange={(e) => setD({ designation: e.target.value })} className={fieldCls("designation")} /></RF>
                  <RF label="Mobile" k="mobile"><input value={draft.mobile} onChange={(e) => setD({ mobile: e.target.value })} className={fieldCls("mobile")} /></RF>
                  <RF label="Email" k="email"><input value={draft.email} onChange={(e) => setD({ email: e.target.value })} className={fieldCls("email")} /></RF>
                  <RF label="GST Number" k="gst_number"><input value={draft.gst_number} onChange={(e) => { const g = e.target.value; setD({ gst_number: g }); }} className={fieldCls("gst_number")} /></RF>
                  <RF label="State" k="state_name"><input value={draft.state_name} onChange={(e) => setD({ state_name: e.target.value })} className={fieldCls("state_name")} placeholder="auto from GST" /></RF>
                  <RF label="State Code" k="state_code"><input value={draft.state_code} onChange={(e) => setD({ state_code: e.target.value })} className={fieldCls("state_code")} /></RF>
                  <div className="sm:col-span-2"><RF label="Billing Address" k="billing_address"><input value={draft.billing_address} onChange={(e) => setD({ billing_address: e.target.value })} className={fieldCls("billing_address")} /></RF></div>
                  <div className="sm:col-span-2"><RF label="Shipping Address" k="shipping_address"><input value={draft.shipping_address} onChange={(e) => setD({ shipping_address: e.target.value })} className={fieldCls("shipping_address")} /></RF></div>
                </div>
              </div>

              {(draft.additional_contacts || []).length > 0 && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Other Contacts (saved to notes)</div>
                  <div className="flex flex-wrap gap-2">
                    {draft.additional_contacts.map((c, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-100 text-xs text-zinc-600 border border-zinc-200">
                        <FileText className="w-3 h-3" />{[c.name, c.designation, c.phone, c.email].filter(Boolean).join(" · ")}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Project (optional)</div>
                  <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer" data-testid="ai-client-alsoproject">
                    <input type="checkbox" checked={alsoProject} onChange={(e) => setAlsoProject(e.target.checked)} className="accent-violet-600" />
                    Also create a draft project
                  </label>
                </div>
                <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 transition-opacity ${alsoProject ? "" : "opacity-40 pointer-events-none"}`}>
                  <RF label="Project Name" k="project_name"><input value={draft.project_name} onChange={(e) => setD({ project_name: e.target.value })} className={fieldCls("project_name")} data-testid="ai-client-projectname" /></RF>
                  <RF label="Location" k="project_location"><input value={draft.project_location} onChange={(e) => setD({ project_location: e.target.value })} className={fieldCls("project_location")} /></RF>
                  <RF label="Category" k="project_category"><input value={draft.project_category} onChange={(e) => setD({ project_category: e.target.value })} className={fieldCls("project_category")} /></RF>
                  <RF label="Estimated Value" k="estimated_value"><input value={draft.estimated_value} onChange={(e) => setD({ estimated_value: e.target.value })} className={fieldCls("estimated_value")} /></RF>
                </div>
              </div>

              {missing.length > 0 && (
                <div className="flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>AI flagged these as missing/unclear: <b>{missing.join(", ")}</b>. Fill them in if you can.</span>
                </div>
              )}
            </div>
          )}
        </div>

        {step === "review" && (
          <div className="flex gap-2 p-5 border-t border-zinc-200 bg-zinc-50 flex-shrink-0">
            <Btn onClick={approve} disabled={busy} data-testid="ai-client-approve" className="bg-violet-600 hover:bg-violet-700">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {busy ? "Creating…" : "Approve & Create Client"}
            </Btn>
            <Btn variant="secondary" onClick={onClose} data-testid="ai-client-reject">Reject &amp; Close</Btn>
          </div>
        )}
      </div>
    </div>
  );
}

function RF({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-zinc-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
